import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/ui/SignaturePad";
import { getPublicEstimateApproval } from "@/utils/estimate-approval.functions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Check, X, Building2, Calendar, FileText, CheckCircle2, XCircle } from "lucide-react";

export const Route = createFileRoute("/estimates/approve/$token")({
  component: PublicApprovalPage,
});

type Estimate = {
  id: string;
  number: string;
  title: string;
  description: string | null;
  issue_date: string;
  expiry_date: string | null;
  currency: string;
  tax_percent: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  notes: string | null;
  terms: string | null;
  clients: { name: string; email: string | null } | null;
  companies: { name: string; logo_url: string | null; email: string | null } | null;
};

type Item = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
};

type TokenRow = {
  id: string;
  estimate_id: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  responded_at: string | null;
  response_notes: string | null;
};

function PublicApprovalPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const [tokenRow, setTokenRow] = useState<TokenRow | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await getPublicEstimateApproval({ data: { token } });
      if (cancelled) return;
      const payload = data as {
        token: TokenRow;
        estimate: Estimate | null;
        items: Item[];
        replacementToken: string | null;
      } | null;
      if (payload?.replacementToken && typeof window !== "undefined") {
        window.location.replace(`/estimates/approve/${payload.replacementToken}`);
        return;
      }
      if (!payload || !payload.token || !payload.estimate) {
        setError(t("pages.estimateApproval.invalidLink"));
        setLoading(false);
        return;
      }
      setTokenRow(payload.token);
      setEstimate(payload.estimate);
      setItems(payload.items ?? []);
      if (payload.estimate?.clients?.name && !signName) {
        setSignName(payload.estimate.clients.name);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const respond = async (decision: "approved" | "rejected") => {
    if (!tokenRow) return;
    if (decision === "approved") {
      if (!signName.trim()) {
        toast.error(t("pages.signature.signerNameRequired"));
        return;
      }
      if (!signature) {
        toast.error(t("pages.signature.signatureRequired"));
        return;
      }
    }
    setSubmitting(true);
    const respondedAt = new Date().toISOString();
    const trimmedNotes = notes.trim() || null;
    const { error } = await supabase.rpc("respond_to_estimate_token", {
      _token: token,
      _decision: decision,
      _response_notes: trimmedNotes ?? "",
      _client_signature_name: decision === "approved" ? signName.trim() : "",
      _client_signature_data: decision === "approved" ? (signature ?? "") : "",
    });
    setSubmitting(false);
    if (error) {
      toast.error(t("pages.estimateApproval.submitFailed"), {
        description: error.message ?? t("pages.estimateApproval.submitFailedDefault"),
      });
      return;
    }
    setTokenRow({
      ...tokenRow,
      status: decision,
      response_notes: trimmedNotes,
      responded_at: respondedAt,
    } as TokenRow);
    toast.success(
      decision === "approved"
        ? t("pages.estimateApproval.approvedToast")
        : t("pages.estimateApproval.rejectedToast"),
    );
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: estimate?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !tokenRow || !estimate) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-3 text-xl font-semibold">
            {t("pages.estimateApproval.linkUnavailable")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? t("pages.estimateApproval.loadFailed")}
          </p>
        </div>
      </div>
    );
  }

  const alreadyResponded = tokenRow.status !== "sent";
  const wasApproved = tokenRow.status === "approved";
  const wasRejected = tokenRow.status === "rejected";
  const wasRevoked = tokenRow.status === "revoked";

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Company header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {estimate.companies?.logo_url ? (
              <img
                src={estimate.companies.logo_url}
                alt={estimate.companies.name}
                className="h-10 w-10 rounded object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold">
                {estimate.companies?.name ?? t("pages.estimateApproval.estimateFallback")}
              </p>
              {estimate.companies?.email && (
                <p className="text-xs text-muted-foreground">{estimate.companies.email}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-xs">
            {estimate.number}
          </Badge>
        </div>

        {/* Status banner */}
        {alreadyResponded && (
          <div
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              wasApproved
                ? "border-primary/30 bg-primary/5"
                : wasRejected
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-border bg-muted"
            }`}
          >
            {wasApproved ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 text-destructive" />
            )}
            <div className="flex-1">
              <p className="font-medium">
                {wasApproved && t("pages.estimateApproval.youApproved")}
                {wasRejected && t("pages.estimateApproval.youDeclined")}
                {wasRevoked && t("pages.estimateApproval.linkRevoked")}
              </p>
              {tokenRow.responded_at && (
                <p className="text-xs text-muted-foreground">
                  {new Date(tokenRow.responded_at).toLocaleString()}
                </p>
              )}
              {wasApproved && signature && (
                <div className="mt-2 rounded border border-border bg-white p-2 inline-block">
                  <img src={signature} alt="signature" className="h-16" />
                  {signName && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("pages.signature.signedAs")}: {signName}
                    </p>
                  )}
                </div>
              )}
              {tokenRow.response_notes && (
                <p className="mt-2 text-sm whitespace-pre-wrap">{tokenRow.response_notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Estimate card */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h1 className="text-2xl font-semibold tracking-tight">{estimate.title}</h1>
          {estimate.clients?.name && (
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.estimateApproval.preparedFor", { name: estimate.clients.name })}
            </p>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.estimateApproval.issued")}
                </p>
                <p className="text-sm font-medium">
                  {new Date(estimate.issue_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.estimateApproval.validUntil")}
                </p>
                <p className="text-sm font-medium">
                  {estimate.expiry_date ? new Date(estimate.expiry_date).toLocaleDateString() : "—"}
                </p>
              </div>
            </div>
          </div>

          {estimate.description && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {t("pages.estimateApproval.scope")}
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{estimate.description}</p>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border p-4">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" /> {t("pages.estimateApproval.lineItems")}
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.estimateApproval.description")}</TableHead>
                  <TableHead className="w-20 text-right">
                    {t("pages.estimateApproval.qty")}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.estimateApproval.unitPrice")}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.estimateApproval.total")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.description}</TableCell>
                    <TableCell className="text-right">{it.quantity}</TableCell>
                    <TableCell className="text-right">{formatMoney(it.unit_price)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(it.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
            <div className="flex w-56 justify-between">
              <span className="text-muted-foreground">{t("pages.estimateApproval.subtotal")}</span>
              <span>{formatMoney(estimate.subtotal)}</span>
            </div>
            <div className="flex w-56 justify-between">
              <span className="text-muted-foreground">
                {t("pages.estimateApproval.taxLabel", { percent: estimate.tax_percent })}
              </span>
              <span>{formatMoney(estimate.tax_amount)}</span>
            </div>
            <div className="mt-1 flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
              <span>{t("pages.estimateApproval.totalLabel")}</span>
              <span>{formatMoney(estimate.total)}</span>
            </div>
          </div>
        </div>

        {(estimate.notes || estimate.terms) && (
          <div className="grid gap-4 sm:grid-cols-2">
            {estimate.notes && (
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.estimateApproval.notes")}
                </p>
                <p className="text-sm whitespace-pre-wrap">{estimate.notes}</p>
              </div>
            )}
            {estimate.terms && (
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.estimateApproval.terms")}
                </p>
                <p className="text-sm whitespace-pre-wrap">{estimate.terms}</p>
              </div>
            )}
          </div>
        )}

        {/* Action panel */}
        {!alreadyResponded && (
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-sm font-semibold">{t("pages.estimateApproval.yourResponse")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("pages.estimateApproval.responseHint")}
            </p>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="signer-name">{t("pages.signature.signerNameLabel")}</Label>
              <Input
                id="signer-name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder={t("pages.signature.signerNamePlaceholder")}
              />
            </div>
            <div className="mt-4">
              <SignaturePad
                value={signature}
                onChange={setSignature}
                label={t("pages.signature.signatureLabel")}
                hint={t("pages.signature.signatureHint")}
                clearLabel={t("pages.signature.signatureClear")}
              />
            </div>
            <Textarea
              placeholder={t("pages.estimateApproval.messagePlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-4"
              rows={3}
            />
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => respond("approved")}
                disabled={submitting}
                className="flex-1 gap-2"
              >
                <Check className="h-4 w-4" />
                {t("pages.estimateApproval.approveBtn")}
              </Button>
              <Button
                onClick={() => respond("rejected")}
                disabled={submitting}
                variant="outline"
                className="flex-1 gap-2"
              >
                <X className="h-4 w-4" />
                {t("pages.estimateApproval.declineBtn")}
              </Button>
            </div>
          </div>
        )}

        <p className="pt-2 text-center text-xs text-muted-foreground">
          {t("pages.estimateApproval.poweredBy", {
            name: estimate.companies?.name ?? "Business Dite Ads",
          })}
        </p>
      </div>
    </div>
  );
}
